/**
 * Map Supabase Auth error messages onto copy a user can act on.
 *
 * Supabase's messages are written for developers ("Invalid login credentials",
 * "Email not confirmed"). Translating them here keeps that vocabulary out of
 * the UI and lets us stay honest when a message is ambiguous.
 */

const MESSAGE_MAP: readonly { match: RegExp; message: string }[] = [
  {
    match: /invalid login credentials/i,
    message: 'Email or password is incorrect.',
  },
  {
    match: /email not confirmed/i,
    message: 'Confirm your email before signing in. Check your inbox for the link.',
  },
  {
    match: /user already registered/i,
    message: 'An account with that email already exists. Sign in instead.',
  },
  {
    match: /password should be at least/i,
    message: 'Password must be at least 8 characters.',
  },
  {
    match: /unable to validate email/i,
    message: 'That does not look like a valid email address.',
  },
  {
    match: /email rate limit/i,
    message: 'Too many emails sent. Please wait a minute and try again.',
  },
  {
    match: /over_request_rate|too many requests/i,
    message: 'Too many attempts. Please wait a moment and try again.',
  },
  {
    match: /same password/i,
    message: 'Choose a password you have not used before.',
  },
  {
    match: /expired|otp has expired/i,
    message: 'This link has expired. Request a new one.',
  },
  {
    match: /network|fetch/i,
    message: 'Could not reach the authentication service. Check your connection.',
  },
];

function messageFromUnknown(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const value = error.message;
    if (typeof value === 'string') return value;
  }
  return '';
}

export function mapAuthError(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const raw = messageFromUnknown(error);
  if (!raw) return fallback;

  for (const entry of MESSAGE_MAP) {
    if (entry.match.test(raw)) return entry.message;
  }

  // Never surface an unrecognised vendor string: it may name internals, and it
  // is rarely actionable. The fallback is honest about the unknown.
  return fallback;
}
